import {
  createContext,
  forwardRef,
  useContext,
  useLayoutEffect,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { Button, type ButtonProps } from "@/components/ui/button";

/**
 * One filled button per card. A card's actions decide at render time what they
 * show (role gates, guest bookings, an expanded form), so each filled action
 * counts itself while mounted and the card outlines Open only while one is on
 * screen.
 */
const Count = createContext<Dispatch<SetStateAction<number>> | null>(null);

export function FilledActionScope({
  count,
  children,
}: {
  count: Dispatch<SetStateAction<number>>;
  children: ReactNode;
}) {
  return <Count.Provider value={count}>{children}</Count.Provider>;
}

/** A filled (default variant) in-card action button. */
export const CardPrimaryButton = forwardRef<HTMLButtonElement, ButtonProps>(
  function CardPrimaryButton(props, ref) {
    const count = useContext(Count);
    // Layout effect: Open is restyled before paint, not after a flash.
    useLayoutEffect(() => {
      if (!count) return;
      count((n) => n + 1);
      return () => count((n) => n - 1);
    }, [count]);
    return <Button ref={ref} {...props} />;
  }
);
